<script setup lang="ts">
import { type AccountContact } from "@/model/app/account-contact"
import type { AddressInterface } from "@/model/app/address-interface"
import type { DebitorProfile } from "@/model/app/debitor-profile"
import { useCountriesStore } from "@/stores/useCountryStore"
import { ref, toRefs, watchEffect } from "vue"

interface SimpleAddress extends AddressInterface {
    name?: string
}

export interface ContactAddressHideOptions {
    name?: true
    addressLine?: true
    addressLine2?: true
    street?: true
    city?: true
    country?: true
}

const props = defineProps<{
    address?: AccountContact | DebitorProfile | SimpleAddress
    oldAddress?: AccountContact | DebitorProfile | SimpleAddress
    hide?: ContactAddressHideOptions
}>()

const { address } = toRefs(props)

const countries = useCountriesStore()

const countryName = ref<string>()

watchEffect(() => {
    countryName.value = ""

    if (address.value?.country) {
        countries.getName(address.value.country).then((name: string) => {
            countryName.value = name
        })
    }
})
</script>

<template>
    <div v-if="address">
        <div v-if="!hide?.name && 'name' in address && address.name">
            <strong v-if="oldAddress && 'name' in oldAddress && address.name != oldAddress.name">
                {{ address.name }}
            </strong>
            <template v-else>{{ address.name }}</template>
        </div>
        <div v-if="!hide?.addressLine && address.addressLine">
            <strong v-if="oldAddress && 'addressLine' in oldAddress && address.addressLine != oldAddress.addressLine">
                {{ address.addressLine }}
            </strong>
            <template v-else>{{ address.addressLine }}</template>
        </div>
        <div v-if="!hide?.street && (address.street || address.streetNumber)">
            <strong v-if="oldAddress && 'street' in oldAddress && address.street != oldAddress.street">
                {{ address.street }}&nbsp;
            </strong>
            <template v-else>{{ address.street }}&nbsp;</template>
            <strong
                v-if="oldAddress && 'streetNumber' in oldAddress && address.streetNumber != oldAddress.streetNumber"
            >
                {{ address.streetNumber }}
            </strong>
            <template v-else>{{ address.streetNumber }}</template>
        </div>
        <div v-if="!hide?.addressLine2 && address.addressLine2">
            <strong
                v-if="oldAddress && 'addressLine2' in oldAddress && address.addressLine2 != oldAddress.addressLine2"
            >
                {{ address.addressLine2 }}
            </strong>
            <template v-else>{{ address.addressLine2 }}</template>
        </div>
        <div v-if="!hide?.city && (address.zip || address.city)">
            <strong v-if="oldAddress && 'country' in oldAddress && address.country != oldAddress.country">
                {{ address.country }}&nbsp;
            </strong>
            <template v-else>{{ address.country }}&nbsp;</template>
            <strong v-if="oldAddress && 'zip' in oldAddress && address.zip != oldAddress.zip">
                {{ address.zip }}
            </strong>
            <template v-else>{{ address.zip }}&nbsp;</template>
            <strong v-if="oldAddress && 'city' in oldAddress && address.city != oldAddress.city">
                {{ address.city }}&nbsp;
            </strong>
            <template v-else>{{ address.city }}</template>
        </div>
        <div v-if="!hide?.country && countryName">
            <strong v-if="oldAddress && 'country' in oldAddress && address.country != oldAddress.country">
                {{ countryName }}
            </strong>
            <template v-else>{{ countryName }}</template>
        </div>
    </div>
</template>
